import type { AxiosResponse } from "axios";
import { type CountryCode, isValidPhoneNumber } from "libphonenumber-js";
import { useCallback, useEffect, useMemo } from "react";
import { z } from "zod";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { SubdivisionField } from "@/shared/components/address/SubdivisionField";
import { ColocaDateField } from "@/shared/components/forms/ColocaDateField";
import { ColocaForm } from "@/shared/components/forms/ColocaForm";
import { ColocaInputField } from "@/shared/components/forms/ColocaInputField";
import { ColocaPhoneField } from "@/shared/components/forms/ColocaPhoneField";
import { ColocaSelectField } from "@/shared/components/forms/ColocaSelectField";
import { useZodForm } from "@/shared/components/forms/useZodForm";
import { useCountryLabel } from "@/shared/hooks/useCountryLabel";
import useTranslations from "@/shared/lang/useTranslations";
import { HttpApiService } from "@/shared/services/httpApi.service";
import { colocaToast } from "@/shared/utils/toast";

import { businessTypesFor } from "../../constants/businessTypes.constants";
import {
  documentLengthMessageKey,
  documentTypeLabelKey,
  documentTypesFor,
  isDocumentNumberLengthValid,
} from "../../constants/identityDocumentTypes.constants";
import { useIsCryptomateOrg } from "../../hooks/useIsCryptomateOrg";
import { IdentitiesService } from "../../services/identitiesService";
import { AddressType as AddressTypeEnum } from "../../types/identities-api.types";
import type {
  CreateBusinessIdentityRequest,
  CreateIdentityResponseDto,
} from "../../types/identity-form.types";
import { countryItems, firstOfferedIdentityCountry, normalizePhone, phoneSeedFor } from "./holder-form.utils";
import { ThreeDsInfoButton } from "./ThreeDsInfoButton";

const buildBusinessSchema = (
  phoneError: string,
  incorporationDateError: string,
  documentLengthError: (code: string) => string | undefined,
) =>
  z
    .object({
      business_type: z.string().min(1),
      legal_name: z.string().min(1).max(255),
      identification_type: z.string().min(1),
      // Mirrors the backend `@Matches` on BusinessIdentityDto.identification_number. The slash belongs to
      // Brazil's CNPJ (12.345.678/0001-95), which is the only fiscal document this form offers for BRA —
      // without it the canonical spelling is rejected here and the holder cannot be created at all.
      identification_number: z
        .string()
        .min(1)
        .max(100)
        .regex(/^[A-Za-z0-9.\-/ ]+$/),
      incorporation_date: z
        .string()
        .min(1)
        .regex(/^\d{4}-\d{2}-\d{2}$/, incorporationDateError),
      email: z.string().email(),
      phone: z.string().min(1),
      phone_country: z.string().length(2),
      country: z.string().length(3),
      owner_name: z.string().min(1).max(255),
      owner_id_type: z.string().min(1),
      owner_id_number: z.string().min(1).max(255),
      owner_country_code: z.string().length(3),
      owner_address_line1: z.string().min(1).max(500),
      owner_city: z.string().min(1).max(255),
      owner_state: z.string().min(1).max(255),
      owner_postal_code: z.string().min(1).max(50),
      address_line_1: z.string().min(1).max(50),
      address_line_2: z.string().max(50).optional().or(z.literal("")),
      city: z.string().min(1).max(50),
      state: z.string().min(1).max(50),
      postal_code: z.string().min(1).max(50),
    })
    .superRefine((data, ctx) => {
      if (!isValidPhoneNumber(data.phone, data.phone_country as CountryCode)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["phone"],
          message: phoneError,
        });
      }

      // Same rule the clients module applies: the API checks the character set but never the length, so a
      // mistyped CNPJ is only caught here. Both screens create the same identity, so both must agree.
      if (!isDocumentNumberLengthValid(data.identification_type, data.identification_number)) {
        const message = documentLengthError(data.identification_type);
        if (message) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["identification_number"],
            message,
          });
        }
      }
      if (!isDocumentNumberLengthValid(data.owner_id_type, data.owner_id_number)) {
        const message = documentLengthError(data.owner_id_type);
        if (message) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["owner_id_number"],
            message,
          });
        }
      }
    });

type BusinessValues = z.infer<ReturnType<typeof buildBusinessSchema>>;

const BUSINESS_DEFAULTS: BusinessValues = {
  business_type: "S.A.S.",
  legal_name: "",
  identification_type: "NIT",
  identification_number: "",
  incorporation_date: "",
  email: "",
  phone: "",
  phone_country: "",
  country: "COL",
  owner_name: "",
  owner_id_type: "CC",
  owner_id_number: "",
  owner_country_code: "COL",
  owner_address_line1: "",
  owner_city: "",
  owner_state: "",
  owner_postal_code: "",
  address_line_1: "",
  address_line_2: "",
  city: "",
  state: "",
  postal_code: "",
};

type BusinessHolderFormProps = {
  formId: string;
  onSuccess: (response?: CreateIdentityResponseDto) => void;
  /**
   * Override the create call. Defaults to `IdentitiesService.createIdentity` (POST /internal/identities). The
   * verification onboarding (marco A) passes a function that posts to `verification/business` with
   * `is_organization_identity: true`.
   */
  submit?: (payload: CreateBusinessIdentityRequest) => Promise<AxiosResponse<CreateIdentityResponseDto>>;
  /** Show the built-in success toast. Defaults to true. */
  showSuccessToast?: boolean;
  onSubmittingChange?: (submitting: boolean) => void;
};

export function BusinessHolderForm({
  formId,
  onSuccess,
  submit,
  showSuccessToast = true,
  onSubmittingChange,
}: BusinessHolderFormProps) {
  const { t, activeLanguage } = useTranslations("cards");
  const showThreeDs = useIsCryptomateOrg();
  const documentLengthError = useCallback(
    (code: string) => {
      const key = documentLengthMessageKey(code);
      return key ? t(key) : undefined;
    },
    [t],
  );
  const businessSchema = useMemo(
    () =>
      buildBusinessSchema(
        t("create_holder_phone_invalid_for_country"),
        t("create_holder_birthdate_invalid"),
        documentLengthError,
      ),
    [t, documentLengthError],
  );
  const countryLabel = useCountryLabel();
  const countryOptions = useMemo(
    () => countryItems(countryLabel, activeLanguage),
    [countryLabel, activeLanguage],
  );
  const { user } = useAuth();
  // The organization's country wins here — this is the company's identity, not the signed-in person's
  // (`PersonHolderForm` reads the same two the other way round for the same reason). Each is checked against
  // the onboarding allowlist in turn, so one we do not offer falls THROUGH to the next rather than ending the
  // search; see `firstOfferedIdentityCountry`.
  const initialCountry =
    firstOfferedIdentityCountry(user?.organization?.country, user?.country) ?? BUSINESS_DEFAULTS.country;
  // The phone follows the same country the two selectors are seeded with, so the field arrives usable: an
  // unresolved country leaves `ColocaPhoneField`'s input disabled, not merely blank.
  const initialPhone = useMemo(() => phoneSeedFor(initialCountry), [initialCountry]);
  const defaults = useMemo(
    () => ({
      ...BUSINESS_DEFAULTS,
      country: initialCountry,
      owner_country_code: initialCountry,
      phone_country: initialPhone.iso2,
      phone: initialPhone.dialCode,
    }),
    [initialCountry, initialPhone],
  );
  const form = useZodForm(businessSchema, defaults);

  // Document types are country-dependent on the backend (`@IsValidDocumentType`).
  // Company doc uses category "fiscal" (the company tax ID / Fiscal ID) keyed on
  // `country`; owner doc uses category "personal" keyed on `owner_country_code`.
  const country = form.watch("country");
  const ownerCountry = form.watch("owner_country_code");
  const companyIdTypeCodes = useMemo(() => documentTypesFor(country, "fiscal"), [country]);
  const ownerIdTypeCodes = useMemo(() => documentTypesFor(ownerCountry, "personal"), [ownerCountry]);
  const companyIdTypeItems = useMemo(
    () => companyIdTypeCodes.map((code) => ({ value: code, label: t(documentTypeLabelKey(code)) })),
    [companyIdTypeCodes, t],
  );
  const ownerIdTypeItems = useMemo(
    () => ownerIdTypeCodes.map((code) => ({ value: code, label: t(documentTypeLabelKey(code)) })),
    [ownerIdTypeCodes, t],
  );
  const businessTypes = useMemo(() => businessTypesFor(country), [country]);
  const businessTypeItems = useMemo(
    () => businessTypes.map(({ value, labelKey }) => ({ value, label: t(labelKey) })),
    [businessTypes, t],
  );

  useEffect(() => {
    const current = form.getValues("business_type");
    if (!businessTypes.some(({ value }) => value === current)) {
      form.setValue("business_type", businessTypes[0].value, { shouldValidate: false });
    }
  }, [businessTypes, form]);

  // Keep each selected document type valid for its country. When a type is actually replaced, the number
  // typed for the old one goes with it: a Colombian NIT left behind under a CNPJ passes every client and
  // server check, because the backend validates the character set and never the semantics. A type that
  // survives the country change keeps its number — nothing about it became wrong.
  useEffect(() => {
    const current = form.getValues("identification_type");
    if (companyIdTypeCodes.length > 0 && !(companyIdTypeCodes as readonly string[]).includes(current)) {
      form.setValue("identification_type", companyIdTypeCodes[0], { shouldValidate: false });
      form.setValue("identification_number", "", { shouldValidate: false });
      form.clearErrors("identification_number");
    }
  }, [companyIdTypeCodes, form]);

  useEffect(() => {
    const current = form.getValues("owner_id_type");
    if (ownerIdTypeCodes.length > 0 && !(ownerIdTypeCodes as readonly string[]).includes(current)) {
      form.setValue("owner_id_type", ownerIdTypeCodes[0], { shouldValidate: false });
      form.setValue("owner_id_number", "", { shouldValidate: false });
      form.clearErrors("owner_id_number");
    }
  }, [ownerIdTypeCodes, form]);

  const isSubmitting = form.formState.isSubmitting;
  useEffect(() => {
    onSubmittingChange?.(isSubmitting);
  }, [isSubmitting, onSubmittingChange]);

  const handleSubmit = async (values: BusinessValues) => {
    const payload: CreateBusinessIdentityRequest = {
      type: "business",
      address: {
        address_line_1: values.address_line_1,
        address_line_2: values.address_line_2?.trim() ?? "",
        city: values.city,
        state: values.state,
        country: values.country,
        type: AddressTypeEnum.REGISTRATION,
        postal_code: values.postal_code,
      },
      identity: {
        legal_name: values.legal_name.trim(),
        business_type: values.business_type,
        country: values.country,
        identification_type: values.identification_type,
        identification_number: values.identification_number.trim(),
        incorporation_date: values.incorporation_date,
        email: values.email.trim(),
        phone: normalizePhone(values.phone),
        owner_name: values.owner_name.trim(),
        owner_id_type: values.owner_id_type,
        owner_id_number: values.owner_id_number.trim(),
        owner_address_line1: values.owner_address_line1.trim(),
        owner_city: values.owner_city.trim(),
        owner_state: values.owner_state.trim(),
        owner_postal_code: values.owner_postal_code.trim(),
        owner_country_code: values.owner_country_code,
      },
    };

    try {
      const submitFn: (
        p: CreateBusinessIdentityRequest,
      ) => Promise<AxiosResponse<CreateIdentityResponseDto>> = submit ?? IdentitiesService.createIdentity;
      const { data } = await submitFn(payload);
      if (showSuccessToast) colocaToast.success(t("create_holder_success"));
      onSuccess(data);
    } catch (err: unknown) {
      colocaToast.error(HttpApiService.getErrorMessage(err) || t("identities_error_load"));
    }
  };

  return (
    <ColocaForm form={form} onSubmit={handleSubmit} id={formId} className="space-y-6">
      <section className="space-y-3">
        <h3 className="text-sm font-semibold text-foreground">
          {t("create_business_identity_section_company_title")}
        </h3>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <ColocaInputField name="legal_name" label={t("create_business_identity_label_legal_name")} />
          <ColocaSelectField
            name="business_type"
            label={t("create_business_identity_label_business_type")}
            items={businessTypeItems}
          />
          <ColocaSelectField
            name="country"
            label={t("create_business_identity_label_country_name")}
            items={countryOptions}
          />
          <ColocaSelectField
            name="identification_type"
            label={t("create_business_identity_label_identification_type")}
            items={companyIdTypeItems}
          />
          <ColocaInputField
            name="identification_number"
            label={t("create_business_identity_label_identification_number")}
          />
          <ColocaDateField
            name="incorporation_date"
            label={t("create_business_identity_label_incorporation_date")}
            maxDate={new Date()}
            fromYear={1900}
            toYear={new Date().getFullYear()}
          />
          <ColocaInputField name="email" label={t("create_identity_label_email")} type="email" />
          <ColocaPhoneField
            name="phone"
            label={
              showThreeDs ? (
                <span className="inline-flex items-center gap-1.5">
                  {t("create_identity_label_phone")}
                  <ThreeDsInfoButton />
                </span>
              ) : (
                t("create_identity_label_phone")
              )
            }
            countryFieldName="phone_country"
            defaultCountry={initialPhone.iso2 || undefined}
          />
        </div>
      </section>

      <section className="space-y-3">
        <h3 className="text-sm font-semibold text-foreground">
          {t("create_business_identity_section_owner_title")}
        </h3>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <ColocaInputField name="owner_name" label={t("create_business_identity_label_owner_name")} />
          <ColocaSelectField
            name="owner_country_code"
            label={t("create_business_identity_label_owner_country_code")}
            items={countryOptions}
          />
          <ColocaSelectField
            name="owner_id_type"
            label={t("create_business_identity_label_owner_id_type")}
            items={ownerIdTypeItems}
          />
          <ColocaInputField
            name="owner_id_number"
            label={t("create_business_identity_label_owner_id_number")}
          />
        </div>
      </section>

      <section className="space-y-3">
        <h3 className="text-sm font-semibold text-foreground">
          {t("create_business_identity_section_owner_address_title")}
        </h3>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <ColocaInputField
            name="owner_address_line1"
            label={t("create_business_identity_label_owner_address_line1")}
          />
          <ColocaInputField name="owner_city" label={t("create_business_identity_label_owner_city")} />
          <SubdivisionField
            name="owner_state"
            countryName="owner_country_code"
            label={t("create_business_identity_label_owner_state")}
          />
          <ColocaInputField
            name="owner_postal_code"
            label={t("create_business_identity_label_owner_postal_code")}
          />
        </div>
      </section>

      <section className="space-y-3">
        <h3 className="text-sm font-semibold text-foreground">
          {t("create_business_identity_section_company_address_title")}
        </h3>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <ColocaInputField name="address_line_1" label={t("create_identity_label_address_line_1")} />
          <ColocaInputField name="address_line_2" label={t("create_identity_label_address_line_2")} />
          <ColocaInputField name="city" label={t("create_identity_label_city")} />
          <SubdivisionField name="state" countryName="country" label={t("create_identity_label_state")} />
          <ColocaInputField name="postal_code" label={t("create_identity_label_postal_code")} />
        </div>
      </section>
    </ColocaForm>
  );
}
